import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AuthCardProps {
    verificationSent: boolean;
    setVerificationSent: (sent: boolean) => void;
}

const AuthCard: React.FC<AuthCardProps> = ({ verificationSent, setVerificationSent }) => {
    const { signInWithGoogle, signup, login, currentUser, logout, resetPassword } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const returnTo = searchParams.get('returnTo');
    const mode = searchParams.get('mode');

    const [isLoginMode, setIsLoginMode] = useState(mode !== 'signup');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    // Sync mode with URL query params
    useEffect(() => {
        setIsLoginMode(mode !== 'signup');
        setError(null);
        setSuccessMessage(null);
        setVerificationSent(false);
    }, [mode, setVerificationSent]);

    // DEV/UAT BYPASS: Allow access without verification if in Dev/UAT mode AND using a test email
    const isDevOrUat = (import.meta as any).env.MODE === 'development' || (import.meta as any).env.MODE === 'uat';
    const emailLower = currentUser?.email?.toLowerCase() || '';
    const isTestUser = emailLower.includes('test') || emailLower.endsWith('@example.com');

    const showVerificationUI = (verificationSent || (currentUser && !currentUser.emailVerified)) && !(isDevOrUat && isTestUser);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);

        if (!isLoginMode && password !== confirmPassword) {
            return setError("Passwords do not match");
        }

        if (password.length < 6) {
            return setError("Password must be at least 6 characters");
        }

        try {
            setLoading(true);
            if (isLoginMode) {
                await login(email, password);
                navigate(returnTo || '/');
            } else {
                await signup(email, password);
                setVerificationSent(true);
            }
        } catch (err: any) {
            console.error(err);
            let msg = "Failed to authenticate";
            if (err.code === 'auth/user-not-found') msg = "No account found with this email";
            if (err.code === 'auth/wrong-password') msg = "Incorrect password";
            if (err.code === 'auth/email-already-in-use') msg = "Email already in use";
            if (err.code === 'auth/weak-password') msg = "Password is too weak";
            if (err.code === 'auth/invalid-credential') msg = "Invalid email or password";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            setLoading(true);
            await signInWithGoogle();
            navigate(returnTo || '/');
        } catch (error) {
            console.error("Login failed", error);
            setError("Failed to sign in with Google");
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!email) {
            return setError("Please enter your email address to reset your password.");
        }
        try {
            setLoading(true);
            setError(null);
            if (resetPassword) {
                await resetPassword(email);
                alert("Password reset email sent! Check your inbox.");
            } else {
                setError("Password reset is not configured.");
            }
        } catch (err: any) {
            console.error(err);
            setError("Failed to reset password. Verify your email address.");
        } finally {
            setLoading(false);
        }
    };

    const toggleMode = () => {
        const nextIsLogin = !isLoginMode;
        setIsLoginMode(nextIsLogin);
        setError(null);
        setSuccessMessage(null);
        setVerificationSent(false);

        const nextParams = new URLSearchParams(searchParams);
        if (!nextIsLogin) {
            nextParams.set('mode', 'signup');
        } else {
            nextParams.delete('mode');
        }
        setSearchParams(nextParams);
    };

    const handleCheckVerification = async () => {
        if (currentUser) {
            setLoading(true);
            try {
                await currentUser.reload();
                if (currentUser.emailVerified) {
                    navigate('/', { replace: true });
                } else {
                    setError("Email not verified yet. Please check your inbox.");
                }
            } catch (e) {
                console.error(e);
                setError("Failed to check status. Try refreshing the page.");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleResendEmail = async () => {
        if (currentUser) {
            try {
                const actionCodeSettings = {
                    url: window.location.origin,
                    handleCodeInApp: false
                };
                await currentUser.sendEmailVerification(actionCodeSettings);
                alert(`Verification email sent to ${currentUser.email}`);
            } catch (e: any) {
                if (e.code === 'auth/too-many-requests') {
                    setError("Too many requests. Please wait a moment before trying again.");
                } else {
                    setError("Failed to resend email.");
                }
            }
        }
    };

    const handleLogout = async () => {
        await logout();
        setVerificationSent(false);
        setIsLoginMode(true);
        setError(null);
        setSuccessMessage(null);
        setEmail('');
        setPassword('');
        setConfirmPassword('');
    };

    return (
        <div className="w-full max-w-md bg-gray-800/90 backdrop-blur-md rounded-xl shadow-2xl border border-gray-700 overflow-hidden flex-shrink-0">
            <div className="p-8">
                {showVerificationUI ? (
                    <div className="text-center space-y-6 animate-fade-in">
                        <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center border border-green-500/50">
                            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-white">Verify Your Email</h2>
                        <div className="text-gray-300">
                            <p>We've sent a verification link to:</p>
                            <p className="font-semibold text-cyan-400 mt-1">{email || currentUser?.email}</p>
                        </div>
                        <p className="text-sm text-gray-400">
                            Please check your inbox (and spam folder) and click the link to activate your account.
                            <br />
                            <span className="text-xs text-gray-500 block mt-2">
                                After verifying, click 'I have verified my email' below, or return here and the page will update automatically.
                            </span>
                        </p>

                        {error && (
                            <div className="bg-red-900/50 border border-red-500 text-red-200 px-3 py-2 rounded text-sm">
                                {error}
                            </div>
                        )}

                        <div className="space-y-3">
                            <button
                                onClick={handleCheckVerification}
                                disabled={loading}
                                className="w-full bg-cyan-600 text-white font-bold py-3 rounded-lg hover:bg-cyan-500 transition-colors shadow-lg disabled:opacity-50"
                            >
                                {loading ? 'Checking...' : 'I have verified my email'}
                            </button>
                            <button
                                onClick={handleResendEmail}
                                className="w-full text-cyan-400 hover:text-cyan-300 text-sm font-medium"
                            >
                                Resend Verification Email
                            </button>
                        </div>

                        <div className="pt-4 border-t border-gray-700">
                            <button
                                onClick={handleLogout}
                                className="text-gray-500 hover:text-white text-sm"
                            >
                                Sign out and return to login
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold text-white mb-2">
                                {isLoginMode ? "Welcome Back" : "Get Started"}
                            </h2>
                            <p className="text-gray-400">
                                {isLoginMode ? "Sign in to access your races" : "Create an account to join the race"}
                            </p>
                        </div>

                        {successMessage && (
                            <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded mb-6 text-sm">
                                {successMessage}
                            </div>
                        )}
                        {error && (
                            <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded mb-6 text-sm">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Email Address</label>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 placeholder-gray-500"
                                    placeholder="name@example.com"
                                />
                            </div>
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-sm font-medium text-gray-300">Password</label>
                                    {isLoginMode && (
                                        <button
                                            type="button"
                                            onClick={handleForgotPassword}
                                            className="text-xs text-cyan-400 hover:text-cyan-300 focus:outline-none hover:underline"
                                        >
                                            Forgot password?
                                        </button>
                                    )}
                                </div>
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 placeholder-gray-500"
                                    placeholder="••••••••"
                                />
                            </div>
                            {!isLoginMode && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Confirm Password</label>
                                    <input
                                        type="password"
                                        required
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 placeholder-gray-500"
                                        placeholder="••••••••"
                                    />
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-cyan-600 text-white font-bold py-3 rounded-lg hover:bg-cyan-500 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Processing...' : (isLoginMode ? 'Sign In' : 'Create Account')}
                            </button>
                        </form>

                        <div className="mt-6 flex items-center">
                            <div className="flex-grow border-t border-gray-600"></div>
                            <span className="mx-4 text-gray-400 text-sm">OR</span>
                            <div className="flex-grow border-t border-gray-600"></div>
                        </div>

                        <div className="mt-6 space-y-4">
                            <button
                                onClick={handleGoogleSignIn}
                                disabled={loading}
                                className="w-full flex items-center justify-center px-4 py-3 bg-white text-gray-900 font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg disabled:opacity-50"
                            >
                                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                    <path
                                        fill="currentColor"
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                    />
                                </svg>
                                Continue with Google
                            </button>
                        </div>

                        <div className="mt-8 text-center">
                            <p className="text-sm text-gray-400">
                                {isLoginMode ? "Don't have an account yet?" : "Already have an account?"}
                                <button
                                    onClick={toggleMode}
                                    className="ml-2 text-cyan-400 font-semibold hover:underline focus:outline-none"
                                >
                                    {isLoginMode ? "Sign Up" : "Sign In"}
                                </button>
                            </p>
                            <p className="text-xs text-gray-500 mt-4 text-center">
                                By continuing, you agree to our{' '}
                                <Link to="/terms" className="text-cyan-400 hover:underline">Terms of Service</Link>
                                ,{' '}
                                <Link to="/eula" className="text-cyan-400 hover:underline">EULA</Link>
                                , and{' '}
                                <Link to="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</Link>.
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AuthCard;
